import Dialog from '@mui/material/Dialog';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { PropsWithChildren, useEffect, useState } from 'react';
import KycVerificationPanel from '../components/molecules/KycVerificationPanel';
import useKycAttempts from '../hooks/useKycAttempts';
import { useStartKycVerification, useUserData } from '../queries';
import { useMarketplaceConfig } from '../queries/marketplace';
import { KYC_RETURN_PENDING_STORAGE_KEY, shouldShowRegistrationKyc } from '../utils/kyc';

const KycVerificationGuard = ({ children }: PropsWithChildren) => {
  const { isAuthenticated } = useAuth();
  const { data: userData, isFetching } = useUserData();
  const { data: marketplaceConfig } = useMarketplaceConfig();
  const { mutate: startVerification, isPending } = useStartKycVerification();
  const { hasReachedLimit } = useKycAttempts(userData?.kycAttempts);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isWaitingForReturnSync, setIsWaitingForReturnSync] = useState(
    () => typeof window !== 'undefined' && sessionStorage.getItem(KYC_RETURN_PENDING_STORAGE_KEY) === 'true'
  );
  const marketplaceKycConfig = marketplaceConfig?.marketplaceConfig.kyc;
  const shouldRequireRegistrationKyc =
    isAuthenticated && Boolean(userData) && shouldShowRegistrationKyc(userData, marketplaceKycConfig);

  useEffect(() => {
    if (!isWaitingForReturnSync || isFetching) {
      return;
    }

    sessionStorage.removeItem(KYC_RETURN_PENDING_STORAGE_KEY);
    setIsWaitingForReturnSync(false);
  }, [isFetching, isWaitingForReturnSync]);

  useEffect(() => {
    if (isWaitingForReturnSync || shouldRequireRegistrationKyc) {
      return;
    }

    sessionStorage.removeItem(KYC_RETURN_PENDING_STORAGE_KEY);
    setIsDialogOpen(false);
    setIsWaitingForReturnSync(false);
  }, [isWaitingForReturnSync, shouldRequireRegistrationKyc]);

  useEffect(() => {
    if (!shouldRequireRegistrationKyc || isDialogOpen || isWaitingForReturnSync) {
      return;
    }

    const openDialog = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDialogOpen(true);
    };

    window.addEventListener('pointerdown', openDialog, true);
    window.addEventListener('keydown', openDialog, true);

    return () => {
      window.removeEventListener('pointerdown', openDialog, true);
      window.removeEventListener('keydown', openDialog, true);
    };
  }, [isDialogOpen, isWaitingForReturnSync, shouldRequireRegistrationKyc]);

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  return (
    <>
      {children}
      <Dialog open={isDialogOpen} onClose={handleClose} maxWidth='sm' fullWidth>
        <KycVerificationPanel
          allowLanguageSelection
          attemptsExceeded={hasReachedLimit}
          isPending={isPending}
          onClose={handleClose}
          onStartVerification={() => startVerification()}
          status={userData?.kycStatus}
          trigger={marketplaceKycConfig?.trigger}
        />
      </Dialog>
    </>
  );
};

export default KycVerificationGuard;
